/**
 * /team — секція «Ролі» (Порція Б.5).
 *
 * Показує системні (read-only) + кастомні ролі поточного tenant'а.
 * (Системних ролей у БД 16; основою для копіювання дозволені лише 11 —
 * див. CANONICAL_SYSTEM_ROLE_CODES.)
 * Дії: створити, редагувати, деактивувати/реактивувати.
 *
 * Захист доступу — `<RequirePermission code="admin.access.manage">`
 * (дублювання backend-enforce для UX; backend все одно поверне 403).
 *
 * Джерело істини — backend:
 *  - 7 заборонених permissions НЕ показуються у формі (вони все одно
 *    дадуть 422 від _validate_permissions_allowed)
 *  - Variant A: основа — лише канонічні 11 дозволених системних ролей-основ
 *    (підмножина системних; UI віддзеркалює CANONICAL_SYSTEM_ROLE_CODES;
 *    backend перевіряє той самий набір)
 *  - Мертві/резервні (COSMETIC_PERMISSIONS) — показуються сірим з підписом
 *
 * Деактивація — М'ЯКА. Діалог: «роль призначено N користувачам, вони
 * втратять права при наступній дії; робота не переривається; роль
 * можна реактивувати». НЕ «видалити назавжди».
 */
import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  accessRoles,
  CANONICAL_SYSTEM_ROLE_CODES,
  FORBIDDEN_IN_CUSTOM_ROLES,
  COSMETIC_PERMISSIONS,
  type AccessRole,
} from '@/api/admin';
import { useConfirm } from '@/components/ConfirmProvider';
import { humanizeBackendError } from '@/utils/backendError';
import RoleEditorDialog from './RoleEditorDialog';

export default function RolesSection() {
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [editorRole, setEditorRole] = useState<AccessRole | 'CREATE' | null>(null);

  const { data: roles, isLoading } = useQuery({
    queryKey: ['access-roles'],
    queryFn: () => accessRoles.list(),
  });

  const { system, customActive, customDeactivated } = useMemo(() => {
    const all = roles ?? [];
    return {
      system: all.filter((r) => r.is_system).sort((a, b) =>
        (a.sort_order ?? 100) - (b.sort_order ?? 100)
      ),
      customActive: all.filter((r) => !r.is_system && r.status === 'ACTIVE')
        .sort((a, b) => a.name.localeCompare(b.name)),
      customDeactivated: all.filter((r) => !r.is_system && r.status === 'DEACTIVATED')
        .sort((a, b) => a.name.localeCompare(b.name)),
    };
  }, [roles]);

  const deactivateMut = useMutation({
    mutationFn: (id: string) => accessRoles.deactivate(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['access-roles'] }),
    onError: (e) => alert(humanizeBackendError(e)),
  });

  const reactivateMut = useMutation({
    mutationFn: (id: string) => accessRoles.reactivate(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['access-roles'] }),
    onError: (e) => alert(humanizeBackendError(e)),
  });

  async function onDeactivate(role: AccessRole) {
    // Беремо assignees щоб показати N у діалозі
    let assigneesCount = 0;
    try {
      const ass = await accessRoles.assignees(role.id);
      assigneesCount = ass.length;
    } catch {
      // непринципово — показуємо діалог без точного числа
    }
    const msg = assigneesCount > 0
      ? `Роль «${role.name}» призначено ${assigneesCount} користувачам. ` +
        `Після деактивації вони втратять пов'язані права при наступній дії в системі. ` +
        `Поточна робота НЕ переривається. Роль можна реактивувати пізніше. Продовжити?`
      : `Деактивувати роль «${role.name}»? Її можна буде реактивувати.`;
    const ok = await confirm({
      message: msg,
      okLabel: 'Деактивувати',
      cancelLabel: 'Скасувати',
    });
    if (ok) deactivateMut.mutate(role.id);
  }

  if (isLoading) return <div>Завантаження ролей…</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
          Системні ролі — read-only. Кастомні створюються копією від системної.
        </div>
        <button
          type="button"
          className="primary"
          onClick={() => setEditorRole('CREATE')}
        >
          + Створити власну роль
        </button>
      </div>

      <h4 style={{ marginTop: '1.5rem' }}>Системні ролі ({system.length})</h4>
      <RolesTable roles={system} readOnly />

      <h4 style={{ marginTop: '1.5rem' }}>Кастомні ролі ({customActive.length})</h4>
      {customActive.length === 0 ? (
        <div style={{ color: 'var(--text-secondary)', padding: '1rem' }}>
          Кастомних ролей ще нема. Натисніть «Створити власну роль».
        </div>
      ) : (
        <RolesTable
          roles={customActive}
          onEdit={(r) => setEditorRole(r)}
          onDeactivate={onDeactivate}
        />
      )}

      {customDeactivated.length > 0 && (
        <details style={{ marginTop: '1.5rem' }}>
          <summary style={{ cursor: 'pointer', color: 'var(--text-secondary)' }}>
            Деактивовані ({customDeactivated.length})
          </summary>
          <div style={{ marginTop: '0.5rem', opacity: 0.6 }}>
            <RolesTable
              roles={customDeactivated}
              onReactivate={(r) => reactivateMut.mutate(r.id)}
            />
          </div>
        </details>
      )}

      {editorRole && (
        <RoleEditorDialog
          role={editorRole === 'CREATE' ? null : editorRole}
          systemRoles={system}
          onClose={() => setEditorRole(null)}
          onSaved={() => {
            setEditorRole(null);
            queryClient.invalidateQueries({ queryKey: ['access-roles'] });
          }}
        />
      )}
    </div>
  );
}

// ============== Таблиця ролей =====================================

function RolesTable({
  roles,
  readOnly = false,
  onEdit,
  onDeactivate,
  onReactivate,
}: {
  roles: AccessRole[];
  readOnly?: boolean;
  onEdit?: (r: AccessRole) => void;
  onDeactivate?: (r: AccessRole) => void;
  onReactivate?: (r: AccessRole) => void;
}) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
          <th style={{ padding: '0.5rem' }}>Назва</th>
          <th style={{ padding: '0.5rem' }}>Code</th>
          <th style={{ padding: '0.5rem' }}>На основі</th>
          <th style={{ padding: '0.5rem' }}>Прав</th>
          {!readOnly && <th style={{ padding: '0.5rem' }}>Дії</th>}
        </tr>
      </thead>
      <tbody>
        {roles.map((r) => (
          <tr key={r.id} style={{ borderBottom: '1px solid var(--border)' }}>
            <td style={{ padding: '0.5rem', fontWeight: r.is_system ? 600 : 400 }}>
              {r.name}
              {r.status === 'DEACTIVATED' && (
                <span style={{ marginLeft: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                  (деактивовано)
                </span>
              )}
            </td>
            <td style={{ padding: '0.5rem', fontFamily: 'monospace', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
              {r.code}
            </td>
            <td style={{ padding: '0.5rem', color: 'var(--text-secondary)' }}>
              {r.is_system ? '—' : r.created_from_role_code ?? '—'}
            </td>
            <td style={{ padding: '0.5rem' }}>{r.permissions.length}</td>
            {!readOnly && (
              <td style={{ padding: '0.5rem' }}>
                {r.status === 'ACTIVE' && onEdit && (
                  <button type="button" onClick={() => onEdit(r)} style={{ marginRight: '0.5rem' }}>
                    Редагувати
                  </button>
                )}
                {r.status === 'ACTIVE' && onDeactivate && (
                  <button type="button" onClick={() => onDeactivate(r)}>
                    Деактивувати
                  </button>
                )}
                {r.status === 'DEACTIVATED' && onReactivate && (
                  <button type="button" onClick={() => onReactivate(r)}>
                    Реактивувати
                  </button>
                )}
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// Експорт констант — для тестів grep-guard.
export { CANONICAL_SYSTEM_ROLE_CODES, FORBIDDEN_IN_CUSTOM_ROLES, COSMETIC_PERMISSIONS };
